import React, { useRef, useState } from 'react';
import api from '../api';

interface BulkOperationsProps {
  onImportComplete: () => void;
  selectedProductIds?: number[];
  userRole?: string;
}

interface ImportResult {
  message: string;
  imported?: number;
  skipped?: number;
  errors?: number;
  failed_rows?: Array<{
    row: number;
    name?: string;
    error: string;
  }>;
}

const BulkOperations: React.FC<BulkOperationsProps> = ({ onImportComplete, selectedProductIds = [], userRole = 'viewer' }) => {
  const isAdmin = userRole === 'admin';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [cleanDb, setCleanDb] = useState(false);
  const [confirmClean, setConfirmClean] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setShowImportModal(true);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/products/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Product_Import_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    // Clean DB is admin-only and requires typed confirmation. Guard here too so
    // the destructive delete can never fire for a non-admin, even if state was
    // somehow toggled.
    const doCleanDb = cleanDb && isAdmin;
    if (doCleanDb && confirmClean !== 'DELETE ALL') return;

    setImporting(true);

    try {
      // Clean DB if requested (admin only)
      if (doCleanDb) {
        await api.delete('/products/bulk-delete-all');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('import_type', 'simple');

      const response = await api.post('/products/import/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data: ImportResult = response.data;
      setImportResult(data);
      setShowImportModal(false);
      setShowResultModal(true);
      setSelectedFile(null);
      setCleanDb(false);
      setConfirmClean('');

      if (data.imported && data.imported > 0) {
        onImportComplete();
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData && typeof errorData === 'object') {
        setImportResult({ message: 'Import failed', ...errorData });
        setShowResultModal(true);
      } else {
        alert('Import failed: ' + (error.response?.data?.detail || error.message));
      }
      setShowImportModal(false);
      setSelectedFile(null);
    } finally {
      setImporting(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const params: any = {};
      if (selectedProductIds.length > 0) {
        params.ids = selectedProductIds.join(',');
      }
      const response = await api.get('/products/export/', { responseType: 'blob', params });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Export failed');
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
      />
      <button className="btn btn-secondary toolbar-btn" onClick={handleDownloadTemplate}>
        <span className="btn-icon">📄</span>
        Template
      </button>
      <button className="btn btn-secondary toolbar-btn" onClick={() => fileInputRef.current?.click()}>
        <span className="btn-icon">📥</span>
        Import
      </button>
      <button className="btn btn-secondary toolbar-btn" onClick={handleExport}>
        <span className="btn-icon">📤</span>
        {selectedProductIds.length > 0 ? `Export (${selectedProductIds.length})` : 'Export'}
      </button>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <button
              className="modal-close"
              onClick={() => { setShowImportModal(false); setSelectedFile(null); setCleanDb(false); setConfirmClean(''); }}
              aria-label="Close"
            >×</button>
            <h3>Import Products</h3>
            <p style={{ margin: '8px 0 16px' }}><strong>File:</strong> {selectedFile?.name}</p>

            {/* Download Template */}
            <div style={{ marginBottom: '1rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDownloadTemplate}
                style={{ fontSize: '12px' }}
              >
                📄 Download Template
              </button>
            </div>

            {/* Format info */}
            <div style={{ padding: '12px 16px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: '#0c4a6e' }}>Expected Format (single sheet):</div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Mandatory columns:</strong>{' '}
                <code style={{ fontSize: '11px', background: '#e0f2fe', padding: '2px 4px', borderRadius: '3px' }}>
                  Name, Department, Category, Subcategory, Price, Brand, Status
                </code>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Optional columns:</strong>{' '}
                <span style={{ fontSize: '12px', color: '#475569' }}>
                  Description, SKU, Vendor, Currency, Images, and any additional attribute columns (Color, Size, HSN, etc.)
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                • Department/Category/SubCategory accept abbreviations (e.g. "EL", "CO", "LT") or full names<br/>
                • SubCategory can be empty if not applicable<br/>
                • Column names are case-insensitive<br/>
                • Any unrecognized column is auto-created as a product attribute<br/>
                • SKUs are auto-generated if not provided
              </div>
            </div>

            {/* Clean DB option — admin only (deletes ALL products) */}
            {isAdmin && (
            <div style={{
              padding: '12px 16px',
              background: cleanDb ? '#fef2f2' : '#f8fafc',
              border: cleanDb ? '2px solid #fca5a5' : '1px solid #e2e8f0',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={cleanDb}
                  onChange={(e) => { setCleanDb(e.target.checked); setConfirmClean(''); }}
                />
                <div>
                  <span style={{ fontWeight: 600, color: cleanDb ? '#dc2626' : '#374151' }}>
                    Clean DB before import
                  </span>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    Delete all existing products before importing. This cannot be undone. Admin only.
                  </div>
                </div>
              </label>

              {cleanDb && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', display: 'block', marginBottom: '4px' }}>
                    Type "DELETE ALL" to confirm:
                  </label>
                  <input
                    type="text"
                    value={confirmClean}
                    onChange={(e) => setConfirmClean(e.target.value)}
                    placeholder="DELETE ALL"
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '13px',
                      border: '1px solid #fca5a5',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              )}
            </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowImportModal(false); setSelectedFile(null); setCleanDb(false); setConfirmClean(''); }}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={importing || (cleanDb && confirmClean !== 'DELETE ALL')}
              >
                {importing ? 'Importing...' : cleanDb ? '⚠️ Clean & Import' : 'Import Products'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultModal && importResult && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Import Results</h3>

            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{importResult.message}</p>
              {importResult.imported !== undefined && (
                <p style={{ margin: '4px 0', color: '#059669', fontSize: '14px' }}>
                  ✓ Imported: {importResult.imported}
                </p>
              )}
              {importResult.skipped !== undefined && importResult.skipped > 0 && (
                <p style={{ margin: '4px 0', color: '#d97706', fontSize: '14px' }}>
                  ⏭ Skipped (existing): {importResult.skipped}
                </p>
              )}
              {importResult.errors !== undefined && importResult.errors > 0 && (
                <p style={{ margin: '4px 0', color: '#dc2626', fontSize: '14px' }}>
                  ✗ Errors: {importResult.errors}
                </p>
              )}
            </div>

            {importResult.failed_rows && importResult.failed_rows.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '8px', color: '#dc2626' }}>Failed Rows</h4>
                <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '8px', maxHeight: '300px', overflow: 'auto' }}>
                  {importResult.failed_rows.map((fr, idx) => (
                    <div key={idx} style={{ marginBottom: '8px', fontSize: '13px', paddingBottom: '8px', borderBottom: idx < importResult.failed_rows!.length - 1 ? '1px solid #fecaca' : 'none' }}>
                      <strong>Row {fr.row}{fr.name ? ` (${fr.name})` : ''}:</strong> {fr.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                onClick={() => { setShowResultModal(false); setImportResult(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkOperations;
